import CustomButton from "@components/buttons/CustomButton";
import CopyButton from "@components/buttons/CopyButton";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import SlidingPanel from "@components/sliders/SlidingPanel";
import { kycV2Service } from "@services/apis/KycV2";
import {
  DIDIT_KYC_MODULES,
  DiditKycDetails,
  DiditKycModule,
} from "@services/models/Kyc/KycV2Models";
import { useEffect, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";
import PrimeDetailsActionButton from "./PrimeDetailsActionButton";

interface DiditKycModulesProps {
  userId?: number | string | null;
  kycDetails?: DiditKycDetails | null;
  onRefresh?: () => void;
}

const APPROVED_STATUSES = new Set(["approved", "completed", "complete"]);

const isApprovedStatus = (status?: string | null) =>
  APPROVED_STATUSES.has(
    String(status ?? "")
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, "_"),
  );

const statusPillClass = (status?: string | null) => {
  const normalized = String(status ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  if (APPROVED_STATUSES.has(normalized)) {
    return "bg-bg-status-green";
  }
  if (
    ["declined", "rejected", "failed", "failure"].includes(normalized)
  ) {
    return "bg-bg-status-red";
  }
  if (!normalized) return "bg-bg-button-secondary";
  return "bg-bg-status-yellow";
};

const formatStatus = (status?: string | null) => {
  const value = String(status ?? "").trim();
  if (!value) return "- -";
  return value.replace(/[_-]+/g, " ");
};

const DiditKycModules = ({
  userId,
  kycDetails,
  onRefresh,
}: DiditKycModulesProps) => {
  const { handleError, handleSuccess } = useSnackbarMessage();
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const [modules, setModules] = useState(kycDetails?.modules);
  const [pendingModule, setPendingModule] = useState<DiditKycModule | null>(
    null,
  );
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const refreshTimer = useRef<number | null>(null);

  useEffect(() => {
    setModules(kycDetails?.modules);
    setPendingModule(null);
    setReason("");
  }, [kycDetails, userId]);

  useEffect(() => {
    return () => {
      if (refreshTimer.current) window.clearTimeout(refreshTimer.current);
    };
  }, []);

  const handleApprove = async () => {
    if (!pendingModule) return;
    const numericUserId = Number(userId);
    if (!Number.isFinite(numericUserId) || numericUserId <= 0) {
      handleError({ message: "userId and module are required." });
      return;
    }

    setLoading(true);
    try {
      const response = await kycV2Service.updateDiditFeatureStatus({
        userId: numericUserId,
        module: pendingModule,
        reason: reason.trim() || undefined,
      });
      handleSuccess({
        message:
          response?.msg ||
          "Successfully sent update request to Didit. Webhook will process the DB update shortly.",
      });
      setModules((current) => ({
        ...current,
        [pendingModule]: {
          ...current?.[pendingModule],
          status: "approved",
        },
      }));
      setPendingModule(null);
      setReason("");
      if (refreshTimer.current) window.clearTimeout(refreshTimer.current);
      refreshTimer.current = window.setTimeout(() => onRefresh?.(), 3000);
    } catch (error: any) {
      handleError({
        message:
          error?.response?.data?.msg ||
          error?.message ||
          "Failed to update Didit status",
      });
    } finally {
      setLoading(false);
    }
  };

  const pendingLabel =
    DIDIT_KYC_MODULES.find((module) => module.key === pendingModule)?.label ??
    pendingModule;

  return (
    <>
      <div className="flex flex-col gap-2">
        <p className="text-text-base text-[11px] font-semibold">
          Verification modules
        </p>
        {DIDIT_KYC_MODULES.map(({ key, label }) => {
          const moduleState = modules?.[key];
          const status = moduleState?.status;
          const approved = isApprovedStatus(status);
          const canApprove = !!String(status ?? "").trim() && !approved;
          const sessionId = moduleState?.sessionId?.trim() || "";

          return (
            <div
              key={key}
              className="flex flex-col gap-2 rounded-md border border-border-subtle px-2 py-2"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-text-base text-[12px] font-medium">
                  {label}
                </span>
                <span
                  className={`inline-flex h-[20px] items-center rounded-lg px-2 text-[10px] font-bold capitalize text-text-base ${statusPillClass(status)}`}
                >
                  {formatStatus(status)}
                </span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] text-text-subtle">
                  {moduleState?.isRequired ? "Required" : "Optional"}
                </span>
                {canApprove && (
                  <PrimeDetailsActionButton
                    label="Approve"
                    icon="verified"
                    skipBusy
                    onClick={() => {
                      setReason("");
                      setPendingModule(key);
                    }}
                  />
                )}
              </div>
              {sessionId && (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] text-text-subtle">
                    Session ID
                  </span>
                  <div className="flex items-center gap-1 min-w-0">
                    <span className="text-text-base text-[11px] font-normal truncate">
                      {sessionId}
                    </span>
                    <CopyButton
                      copyText={sessionId}
                      copyMessage="Session ID copied"
                      copyIconSize={10}
                      buttonSize="14px"
                      borderRadius="2px"
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <SlidingPanel
        isOpen={!!pendingModule}
        onClose={loading ? () => {} : () => setPendingModule(null)}
        onClickCross={loading ? () => {} : () => setPendingModule(null)}
        heading={`Approve ${pendingLabel}`}
        showConfirm={false}
        showCross={!loading}
        isMobile={isMobile}
      >
        <div className="p-4 flex flex-col gap-4">
          <p className="text-xs text-text-subtle">
            You are about to manually approve{" "}
            <strong className="text-text-base">{pendingLabel}</strong> for user{" "}
            <strong className="text-text-base">{userId ?? "- -"}</strong>. This
            sends an approval to Didit. The local status updates after the
            webhook arrives.
          </p>
          <GlobalTextInputField
            title="Reason (optional)"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Manually verified by compliance"
            fontSize="12px"
            backgroundColor="bg-bg-page-body"
            inputBorder="border-border-subtle rounded-md"
          />
          <div className="flex flex-col gap-2">
            <CustomButton
              text="APPROVE"
              isActive={!loading}
              isButtonLoading={loading}
              onClick={handleApprove}
            />
            <CustomButton
              text="CANCEL"
              isActive={!loading}
              isHollow
              onClick={() => setPendingModule(null)}
            />
          </div>
        </div>
      </SlidingPanel>
    </>
  );
};

export default DiditKycModules;
